import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  ArrowLeft,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  FileChartColumn,
  Flag,
  GitCompareArrows,
  Home,
  Info,
  LockKeyhole,
  NotebookTabs,
  Play,
  ShieldCheck,
  UserRound,
  WifiOff,
  X,
} from 'lucide-react';
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  adMode,
  canShowInterstitial,
  isRaceReportUnlocked,
  showInterstitialAd,
  showRewardedAd,
  unlockRaceReport,
} from '../ads/ad-service';
import type { AnalysisStatus, Entry, Horse, Jockey, Odds, OddsSnapshot, Race, Report } from '../domain';
import { formatDateTime, formatKoreanDate, formatPrize, formatRaceStatus, formatRank } from '../lib/format';
import horseBayImage from '../assets/horses/horse-bay.jpg';
import horseBlackImage from '../assets/horses/horse-black.jpg';
import horseChestnutImage from '../assets/horses/horse-chestnut.jpg';
import { useAppData, useAppLoading } from './app-context';

type RaceTab = 'analysis' | 'compare' | 'results' | 'info';
const legacyRaceTabs: Record<string, RaceTab> = {
  report: 'analysis',
  entries: 'compare',
  overview: 'info',
  odds: 'results',
};
const horseImages = [horseBayImage, horseBlackImage, horseChestnutImage];

const horseImageForId = (horseId: string): string => {
  const hash = [...horseId].reduce((value, character) => ((value * 31) + character.charCodeAt(0)) >>> 0, 0);
  return horseImages[hash % horseImages.length];
};

const HorseDetailLink = ({
  race,
  gate,
  children,
  className = '',
}: {
  race: Race;
  gate: number;
  children: ReactNode;
  className?: string;
}) => {
  const entry = race.entries.find((item) => item.gate === gate);
  if (!entry) return <span className={className}>{children}</span>;
  return (
    <Link
      className={`horse-name-link ${className}`.trim()}
      to={`/horse/${entry.horseId}?race=${race.id}`}
      aria-label={`${entry.horseName} 상세 정보`}
    >
      {children}
      <ChevronRight size={17} />
    </Link>
  );
};

const LoadingScreen = () => (
  <div className="app-frame loading-screen" aria-live="polite">
    <span className="loading-mark"><NotebookTabs size={24} /></span>
    <strong>말랑노트</strong>
    <span>데이터를 확인하고 있어요</span>
  </div>
);

const AppHeader = () => {
  const { data, origin } = useAppData();
  const { pathname } = useLocation();
  const title = pathname === '/races' ? '경주' : '홈';

  return (
    <header className="app-header">
      <h1>{title}</h1>
      {(data.meta.isSample || origin === 'sample') && <span className="sample-chip">샘플</span>}
    </header>
  );
};

const BottomNavigation = () => {
  const items = [
    { to: '/', label: '홈', icon: Home, end: true },
    { to: '/races', label: '경주', icon: Flag, end: false },
  ];
  return (
    <nav className="bottom-nav" aria-label="주요 메뉴">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} className={({ isActive }) => (isActive ? 'active' : undefined)}>
          <Icon size={21} />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

const MainLayout = () => (
  <div className="app-frame">
    <AppHeader />
    <main className="main-content"><Outlet /></main>
    <BottomNavigation />
  </div>
);

const DetailLayout = ({ children, title, subtitle, onBack }: { children: ReactNode; title: string; subtitle?: string; onBack?: () => void }) => {
  const navigate = useNavigate();
  return (
    <div className="app-frame detail-frame">
      <header className="detail-header">
        <button className="icon-button" type="button" onClick={onBack ?? (() => navigate(-1))} aria-label="이전 화면">
          <ArrowLeft size={23} />
        </button>
        <div>
          <strong>{title}</strong>
          {subtitle && <span>{subtitle}</span>}
        </div>
        <span className="header-spacer" aria-hidden="true" />
      </header>
      <main className="detail-content">{children}</main>
    </div>
  );
};

const NoticeBand = () => {
  const { data, origin, warning } = useAppData();
  if (!data.meta.isSample && !warning && origin !== 'cache') return null;

  const isWarning = Boolean(warning || origin === 'cache');
  return (
    <div className={`notice-band ${isWarning ? 'warning' : 'sample'}`} role="status">
      {isWarning ? <WifiOff size={18} /> : <Info size={18} />}
      <span>{warning ?? data.meta.notice}</span>
    </div>
  );
};

const SectionHeading = ({ title, action }: { title: string; action?: ReactNode }) => (
  <div className="section-heading">
    <h2>{title}</h2>
    {action}
  </div>
);

const StatusBadge = ({ status }: { status: Race['status'] }) => (
  <span className={`status-badge ${status}`}>{formatRaceStatus(status)}</span>
);

const analysisStatusCopy: Record<AnalysisStatus, { label: string; detail: string }> = {
  ready: { label: '분석 준비 완료', detail: '현재 출전표 기준 분석을 확인할 수 있어요.' },
  pending: { label: '분석 준비 중', detail: '공개된 출전 정보를 확인하고 있어요.' },
  insufficient: { label: '표본 부족', detail: '신뢰할 수 있는 확률을 계산하기에 기록이 부족해요.' },
  stale: { label: '출전 변경 확인 중', detail: '변경된 출전표에 맞춰 분석을 다시 확인하고 있어요.' },
  unavailable: { label: '분석 제공 안 함', detail: '취소 또는 데이터 조건 미충족으로 분석을 제공하지 않아요.' },
};

const AnalysisStatusBadge = ({ status }: { status: AnalysisStatus }) => (
  <span className={`analysis-status-badge ${status}`}>{analysisStatusCopy[status].label}</span>
);

const ConfirmedOddsGrid = ({ odds, compact = false }: { odds: Odds[]; compact?: boolean }) => (
  <div className={`confirmed-odds-grid${compact ? ' compact' : ''}`} aria-label="확정 조합·배당">
    {odds.map((item) => (
      <div className="confirmed-odds-item" key={`${item.type}-${item.combination}`}>
        <span>{item.label}</span>
        <strong>{item.combination}</strong>
        <b>{item.capped ? `${item.dividend.toFixed(1)}배 이상` : `${item.dividend.toFixed(1)}배`}</b>
      </div>
    ))}
  </div>
);

const oddsPoolOrder: Odds['type'][] = ['WIN', 'PLC', 'QNL', 'EXA', 'QPL', 'TLA', 'TRI'];

const OddsSnapshotPanel = ({ snapshot, race }: { snapshot: OddsSnapshot; race: Race }) => {
  const availablePools = oddsPoolOrder.filter((type) => snapshot.entries.some((item) => item.type === type));
  const [activePool, setActivePool] = useState<Odds['type']>(availablePools[0] ?? 'WIN');

  useEffect(() => {
    setActivePool(availablePools[0] ?? 'WIN');
  }, [race.id]);

  const entries = snapshot.entries.filter((item) => item.type === activePool);
  const heading = snapshot.state === 'final'
    ? '확정 배당'
    : snapshot.state === 'collected'
      ? '수집 배당'
      : '배당 현황';

  return (
    <section className="odds-snapshot-section" aria-labelledby="odds-snapshot-title">
      <div className="odds-snapshot-heading">
        <div>
          <h2 id="odds-snapshot-title">{heading}</h2>
          <span>{formatDateTime(snapshot.collectedAt)} 기준 · 주요 배당</span>
        </div>
        {snapshot.state !== 'final' && <span className="odds-live-badge">변동 가능</span>}
      </div>
      <div className="odds-pool-tabs" role="tablist" aria-label="승식 선택">
        {availablePools.map((type) => {
          const label = snapshot.entries.find((item) => item.type === type)?.label ?? type;
          return (
            <button
              key={type}
              type="button"
              role="tab"
              aria-selected={activePool === type}
              className={activePool === type ? 'active' : ''}
              onClick={() => setActivePool(type)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="odds-quote-list">
        {entries.map((item) => {
          const gate = Number(item.combination);
          const horseName = Number.isInteger(gate)
            ? race.entries.find((entry) => entry.gate === gate)?.horseName
            : undefined;
          return (
            <div className="odds-quote-row" key={`${item.type}-${item.combination}`}>
              <div>
                <strong>{item.combination}</strong>
                {horseName && (
                  <HorseDetailLink race={race} gate={gate}>
                    <span>{horseName}</span>
                  </HorseDetailLink>
                )}
              </div>
              <b>{item.capped ? `${item.dividend.toFixed(1)}배 이상` : `${item.dividend.toFixed(1)}배`}</b>
            </div>
          );
        })}
      </div>
      <p className="panel-footnote">
        단승·연승은 출전마별로, 조합식은 수집 시점의 낮은 배당 상위 3개만 보여드려요.
      </p>
    </section>
  );
};

const RaceListRow = ({ race }: { race: Race }) => (
  <Link className="list-row race-row" to={`/race/${race.id}?tab=${race.status === 'completed' ? 'results' : 'analysis'}`} state={{ backTo: '/' }}>
    <div className="race-number"><strong>{race.number}</strong><span>R</span></div>
    <div className="row-main">
      <div className="row-title-line">
        <strong>{race.meet} {race.number}경주</strong>
        <StatusBadge status={race.status} />
      </div>
      <span>{formatKoreanDate(race.date)} · {race.startTime} · {race.distance}m</span>
      <small>{race.grade} · {race.entries.length}두</small>
    </div>
    <ChevronRight size={20} className="row-chevron" aria-hidden="true" />
  </Link>
);

const FeaturedAnalysis = ({ race }: { race: Race }) => (
  <section className="featured-analysis" aria-labelledby="featured-analysis-title">
    <div className="featured-analysis-top">
      <div>
        <span>다음 출발</span>
        <time dateTime={`${race.date}T${race.startTime}`}>{race.startTime}</time>
      </div>
      <span className={`meet-chip meet-${race.meet}`}>{race.meet}</span>
    </div>
    <div className="featured-analysis-title">
      <div>
        <h2 id="featured-analysis-title">{race.meet} {race.number}경주</h2>
        <p>{race.distance}m · {race.grade} · 출전 {race.entries.length}두</p>
      </div>
      <AnalysisStatusBadge status={race.analysisStatus} />
    </div>
    <Link
      className="primary-button full-width featured-report-cta"
      to={`/race/${race.id}?tab=analysis`}
      state={{ backTo: '/' }}
    >
      분석 리포트 보기
      <FileChartColumn size={19} />
    </Link>
    <Link
      className="featured-compare-link"
      to={`/race/${race.id}?tab=compare`}
      state={{ backTo: '/' }}
    >
      <GitCompareArrows size={18} />
      출전마 직접 비교
      <ChevronRight size={18} />
    </Link>
  </section>
);

const sortRacesByStart = (a: Race, b: Race) => (
  `${a.date}T${a.startTime}`.localeCompare(`${b.date}T${b.startTime}`)
  || a.meet.localeCompare(b.meet)
  || a.number - b.number
);

const getKoreanTodayKey = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
};

const RaceFlowCard = ({ race, index }: { race: Race; index: number }) => {
  const horseNames = race.entries.slice(0, 3).map((entry) => entry.horseName).join(' · ');
  const remaining = Math.max(0, race.entries.length - 3);
  return (
    <Link
      className="race-flow-card"
      data-race-index={index}
      to={`/race/${race.id}?tab=analysis`}
      state={{ backTo: '/races' }}
      aria-label={`${race.startTime} ${race.meet} ${race.number}경주 상세 보기`}
    >
      <div className="race-flow-card-top">
        <time dateTime={`${race.date}T${race.startTime}`}>{race.startTime}</time>
        <span className={`meet-chip meet-${race.meet}`}>{race.meet}</span>
      </div>
      <div className="race-flow-title">
        <strong>{race.meet} {race.number}경주</strong>
        <StatusBadge status={race.status} />
      </div>
      <div className="race-flow-metrics">
        <strong>{race.distance}m</strong>
        <span>{race.grade}</span>
        <span>출전 {race.entries.length}두</span>
      </div>
      <div className="race-flow-horses">
        <span>출전마</span>
        <strong>{horseNames}{remaining > 0 ? ` 외 ${remaining}두` : ''}</strong>
      </div>
      <div className="race-flow-card-footer">
        <span>분석 리포트 보기</span>
        <ChevronRight size={21} aria-hidden="true" />
      </div>
    </Link>
  );
};

interface ModalSheetProps {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  busy?: boolean;
  footer: ReactNode;
}

const ModalSheet = ({ open, title, children, onClose, busy = false, footer }: ModalSheetProps) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
      if (event.key !== 'Tab' || !sheetRef.current) return;
      const focusable = [...sheetRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], select, [tabindex]:not([tabindex="-1"])')];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [busy, onClose, open]);

  if (!open) return null;
  return (
    <div className="sheet-layer">
      <div className="sheet-backdrop" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <section ref={sheetRef} className="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-title">
        <div className="sheet-header">
          <h2 id="sheet-title">{title}</h2>
          <button ref={closeRef} className="icon-button" type="button" onClick={onClose} disabled={busy} aria-label="닫기">
            <X size={21} />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        <div className="sheet-footer">{footer}</div>
      </section>
    </div>
  );
};

const InterstitialAction = ({
  label,
  onContinue,
  className = 'secondary-button',
  sheetTitle = '상세 정보로 이동',
  description = '다음 화면으로 이동하기 전에 전면 광고가 표시될 수 있어요.',
}: {
  label: string;
  onContinue: () => void;
  className?: string;
  sheetTitle?: string;
  description?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const start = () => {
    if (!canShowInterstitial()) {
      onContinue();
      return;
    }
    setOpen(true);
  };

  const proceed = async () => {
    setBusy(true);
    const outcome = await showInterstitialAd();
    setBusy(false);
    if (outcome === 'failed') {
      setOpen(false);
      onContinue();
      return;
    }
    setOpen(false);
    onContinue();
  };

  return (
    <>
      <button type="button" className={className} onClick={start}>{label}<ChevronRight size={18} /></button>
      <ModalSheet
        open={open}
        title={sheetTitle}
        busy={busy}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button className="text-button" type="button" onClick={() => setOpen(false)} disabled={busy}>취소</button>
            <button className="primary-button" type="button" onClick={proceed} disabled={busy}>{busy ? '준비 중' : '계속'}</button>
          </>
        }
      >
        <p>{description}</p>
        <p className="muted-copy">전면 광고는 앱 전체에서 최소 3분 간격을 지켜요.</p>
        {adMode === 'web-preview' && <p className="preview-note">웹 검수에서는 광고 완료 이벤트만 시뮬레이션해요.</p>}
      </ModalSheet>
    </>
  );
};

const HomePage = () => {
  const { data } = useAppData();
  const navigate = useNavigate();
  const upcoming = useMemo(
    () => data.races.filter((race) => race.status === 'scheduled').sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`)),
    [data.races],
  );
  const latestResult = useMemo(
    () => data.races.filter((race) => race.status === 'completed').sort((a, b) => b.date.localeCompare(a.date))[0],
    [data.races],
  );
  return (
    <>
      <NoticeBand />

      <section className="content-section analysis-home-section">
        <SectionHeading title="다음 경주 분석" action={<Link to="/races">전체 보기</Link>} />
        {upcoming[0]
          ? <FeaturedAnalysis race={upcoming[0]} />
          : <EmptyState title="예정 경주가 없어요" detail="새 출전표가 공개되면 반영할게요." />}
      </section>

      {upcoming.length > 1 && (
        <section className="content-section upcoming-section">
          <SectionHeading title="이어지는 경주" />
          <div className="list-block">
            {upcoming.slice(1, 3).map((race) => <RaceListRow key={race.id} race={race} />)}
          </div>
        </section>
      )}

      <section className="content-section result-section">
        <SectionHeading title="최근 확정 배당" />
        {latestResult && latestResult.odds.length > 0 ? (
          <div className="result-summary">
            <div className="result-topline">
              <div><span>{formatKoreanDate(latestResult.date)}</span><strong>{latestResult.meet} {latestResult.number}경주</strong></div>
              <StatusBadge status="completed" />
            </div>
            <div className="home-odds-summary">
              <div><strong>확정 조합·배당</strong><span>공식 결과</span></div>
              <ConfirmedOddsGrid odds={latestResult.odds} compact />
            </div>
            <InterstitialAction
              label="확정 배당 상세 보기"
              sheetTitle="결과·배당 상세로 이동"
              description="확정 결과와 배당 상세로 이동하기 전에 전면 광고가 표시될 수 있어요."
              onContinue={() => navigate(`/race/${latestResult.id}?tab=results`, { state: { backTo: '/' } })}
            />
          </div>
        ) : (
          <EmptyState title="확정 배당이 없어요" detail="검증된 배당이 게시되면 여기에 표시해요." />
        )}
      </section>

      <AnalysisDisclaimer />
    </>
  );
};

const EmptyState = ({ title, detail }: { title: string; detail: string }) => (
  <div className="empty-state">
    <CircleAlert size={23} />
    <strong>{title}</strong>
    <span>{detail}</span>
  </div>
);

const RacesPage = () => {
  const { data } = useAppData();
  const railRef = useRef<HTMLDivElement>(null);
  const today = useMemo(getKoreanTodayKey, []);
  const scheduledRaces = useMemo(
    () => data.races.filter((race) => race.status === 'scheduled').sort(sortRacesByStart),
    [data.races],
  );
  const raceDates = useMemo(() => [...new Set(scheduledRaces.map((race) => race.date))], [scheduledRaces]);
  const recommendedDate = raceDates.includes(today) ? today : (raceDates.find((date) => date > today) ?? raceDates[0] ?? '');
  const [selectedDate, setSelectedDate] = useState(recommendedDate);
  const [activeIndex, setActiveIndex] = useState(0);
  const races = scheduledRaces.filter((race) => race.date === selectedDate);

  useEffect(() => {
    if (!raceDates.includes(selectedDate)) setSelectedDate(recommendedDate);
  }, [raceDates, recommendedDate, selectedDate]);

  useEffect(() => {
    setActiveIndex(0);
    railRef.current?.scrollTo?.({ left: 0, behavior: 'auto' });
  }, [selectedDate]);

  const moveToRace = (nextIndex: number) => {
    if (!railRef.current || races.length === 0) return;
    const safeIndex = Math.max(0, Math.min(nextIndex, races.length - 1));
    const card = railRef.current.querySelector<HTMLElement>(`[data-race-index="${safeIndex}"]`);
    setActiveIndex(safeIndex);
    if (card) railRef.current.scrollTo?.({ left: Math.max(0, card.offsetLeft - 20), behavior: 'smooth' });
  };

  const updateActiveRace = () => {
    const rail = railRef.current;
    if (!rail) return;
    const center = rail.scrollLeft + rail.clientWidth / 2;
    const cards = [...rail.querySelectorAll<HTMLElement>('[data-race-index]')];
    const closest = cards.reduce((best, card) => (
      Math.abs(card.offsetLeft + card.offsetWidth / 2 - center)
        < Math.abs(best.offsetLeft + best.offsetWidth / 2 - center) ? card : best
    ), cards[0]);
    if (closest) setActiveIndex(Number(closest.dataset.raceIndex));
  };

  return (
    <>
      <NoticeBand />
      {raceDates.length > 1 && (
        <div className="race-day-tabs" role="tablist" aria-label="경주일 선택">
          {raceDates.map((date) => (
            <button key={date} type="button" role="tab" aria-selected={selectedDate === date} className={selectedDate === date ? 'selected' : ''} onClick={() => setSelectedDate(date)}>
              <strong>{date === today ? '오늘' : formatKoreanDate(date)}</strong>
              <span>{date === today ? formatKoreanDate(date) : '경주일'}</span>
            </button>
          ))}
        </div>
      )}
      <section className="race-flow-section">
        <div className="race-flow-heading">
          <div>
            <span>{selectedDate === today ? '오늘' : '다음 경주일'}</span>
            <h2>{selectedDate ? formatKoreanDate(selectedDate) : '공개된 일정 없음'}</h2>
          </div>
          {races.length > 0 && <strong>{activeIndex + 1} / {races.length}</strong>}
        </div>
        {races.length > 0 ? (
          <>
            <div ref={railRef} className="race-flow-rail" onScroll={updateActiveRace}>
              {races.map((race, index) => <RaceFlowCard key={race.id} race={race} index={index} />)}
            </div>
            <div className="race-flow-controls">
              <button type="button" className="icon-button" onClick={() => moveToRace(activeIndex - 1)} disabled={activeIndex === 0} aria-label="이전 경주" title="이전 경주">
                <ChevronLeft size={24} />
              </button>
              <span>좌우로 넘겨 다음 경주를 확인하세요</span>
              <button type="button" className="icon-button" onClick={() => moveToRace(activeIndex + 1)} disabled={activeIndex === races.length - 1} aria-label="다음 경주" title="다음 경주">
                <ChevronRight size={24} />
              </button>
            </div>
          </>
        ) : <EmptyState title="공개된 당일 경주가 없어요" detail="경주계획이 게시되면 시간 순서대로 보여드려요." />}
      </section>
    </>
  );
};

const RaceDetailPage = () => {
  const { raceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lastSequenceMoveAt = useRef(0);
  const { data } = useAppData();
  const race = data.races.find((item) => item.id === raceId);
  const requestedTab = searchParams.get('tab');

  if (!race) return <Navigate to="/races" replace />;

  const normalizedTab = requestedTab ? legacyRaceTabs[requestedTab] ?? requestedTab : null;
  const defaultTab: RaceTab = race.status === 'completed' ? 'results' : 'analysis';
  const activeTab: RaceTab = ['analysis', 'compare', 'results', 'info'].includes(normalizedTab ?? '')
    ? normalizedTab as RaceTab
    : defaultTab;
  const tabs: { id: RaceTab; label: string }[] = [
    { id: 'analysis', label: '분석' }, { id: 'compare', label: '출전·비교' },
    { id: 'results', label: '결과·배당' }, { id: 'info', label: '정보' },
  ];
  const dayRaces = data.races
    .filter((item) => item.status === 'scheduled' && item.date === race.date)
    .sort(sortRacesByStart);
  const sequenceIndex = dayRaces.findIndex((item) => item.id === race.id);
  const previousRace = sequenceIndex > 0 ? dayRaces[sequenceIndex - 1] : null;
  const nextRace = sequenceIndex >= 0 && sequenceIndex < dayRaces.length - 1 ? dayRaces[sequenceIndex + 1] : null;
  const backTo = (location.state as { backTo?: string } | null)?.backTo ?? '/races';
  const openRace = (target: Race | null) => {
    const now = Date.now();
    if (!target || now - lastSequenceMoveAt.current < 600) return;
    lastSequenceMoveAt.current = now;
    navigate(`/race/${target.id}?tab=${activeTab}`, { replace: true, state: { backTo } });
  };

  return (
    <DetailLayout
      title={`${race.meet} ${race.number}경주`}
      subtitle={`${formatKoreanDate(race.date)} · ${race.startTime}`}
      onBack={() => navigate(backTo, { replace: true })}
    >
      <NoticeBand />
      {sequenceIndex >= 0 && dayRaces.length > 1 && (
        <nav className="race-sequence-nav" aria-label="당일 경주 시간순 이동">
          <button type="button" onClick={() => openRace(previousRace)} disabled={!previousRace} aria-label={previousRace ? `이전 경주 ${previousRace.startTime} ${previousRace.meet}` : '이전 경주 없음'}>
            <ChevronLeft size={21} />
            <span><small>이전</small><strong>{previousRace ? previousRace.startTime : '처음'}</strong></span>
          </button>
          <div><strong>{sequenceIndex + 1} / {dayRaces.length}</strong><span>시간순</span></div>
          <button type="button" onClick={() => openRace(nextRace)} disabled={!nextRace} aria-label={nextRace ? `다음 경주 ${nextRace.startTime} ${nextRace.meet}` : '다음 경주 없음'}>
            <span><small>다음</small><strong>{nextRace ? nextRace.startTime : '마지막'}</strong></span>
            <ChevronRight size={21} />
          </button>
        </nav>
      )}
      <section className="race-hero">
        <div><span>{race.grade}</span><StatusBadge status={race.status} /></div>
        <h1>{race.distance}m</h1>
        <p>{race.title} · {race.entries.length}두</p>
      </section>
      <div className="tab-strip" role="tablist" aria-label="경주 상세">
        {tabs.map((tab) => (
          <button key={tab.id} type="button" role="tab" aria-selected={activeTab === tab.id} className={activeTab === tab.id ? 'active' : ''} onClick={() => setSearchParams({ tab: tab.id }, { replace: true })}>
            {tab.label}
          </button>
        ))}
      </div>
      <section className="tab-panel" role="tabpanel">
        {activeTab === 'analysis' && <AnalysisPanel race={race} />}
        {activeTab === 'compare' && <EntriesPanel race={race} />}
        {activeTab === 'results' && <ResultsPanel race={race} />}
        {activeTab === 'info' && <RaceOverview race={race} />}
      </section>
    </DetailLayout>
  );
};

const TableRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="table-row"><span>{label}</span><strong>{value}</strong></div>
);

const RaceOverview = ({ race }: { race: Race }) => (
  <>
    <SectionHeading title="경주 정보" />
    <div className="table-block">
      <TableRow label="출발" value={`${formatKoreanDate(race.date)} ${race.startTime}`} />
      <TableRow label="조건" value={`${race.ageCondition} · ${race.weightCondition}`} />
      <TableRow label="등급" value={race.grade} />
      <TableRow label="1착 상금" value={formatPrize(race.prize)} />
      <TableRow label="날씨" value={race.weather ?? '경기 후 업데이트'} />
      <TableRow label="주로" value={race.track ?? '경기 후 업데이트'} />
    </div>
    <div className="context-note"><ShieldCheck size={18} /><span>사전 정보와 D+1 확정 기록을 구분해 보관해요.</span></div>
  </>
);

const formatRecentRanks = (horse: Horse | undefined): string => {
  if (!horse || horse.recentRuns.length === 0) return '정보 없음';
  return horse.recentRuns.slice(0, 3).map((run) => run.rank ? `${run.rank}위` : '제외').join(' · ');
};

const formatTop3Rate = (runs: Horse['recentRuns']): string => {
  const validRuns = runs.filter((run) => run.rank !== null);
  if (validRuns.length === 0) return '정보 없음';
  const top3 = validRuns.filter((run) => (run.rank ?? 99) <= 3).length;
  return `${Math.round((top3 / validRuns.length) * 100)}% (${validRuns.length}회)`;
};

const EntriesPanel = ({ race }: { race: Race }) => {
  const { data } = useAppData();
  const [selectedGates, setSelectedGates] = useState<number[]>(() => race.entries.slice(0, 2).map((entry) => entry.gate));

  useEffect(() => {
    setSelectedGates(race.entries.slice(0, 2).map((entry) => entry.gate));
  }, [race.id, race.entries]);

  const toggleEntry = (gate: number) => {
    setSelectedGates((current) => {
      if (current.includes(gate)) {
        return current.filter((item) => item !== gate);
      }
      return current.length >= 4 ? current : [...current, gate].sort((a, b) => a - b);
    });
  };

  const selected = selectedGates
    .map((gate) => race.entries.find((entry) => entry.gate === gate))
    .filter((entry): entry is Entry => Boolean(entry))
    .map((entry) => ({
      entry,
      horse: data.horses.find((horse) => horse.id === entry.horseId),
      jockey: data.jockeys.find((jockey) => jockey.id === entry.jockeyId),
    }));

  const comparisonRows = [
    {
      label: '최근 3회',
      values: selected.map(({ horse }) => formatRecentRanks(horse)),
    },
    {
      label: '최근 3위권',
      values: selected.map(({ horse }) => formatTop3Rate(horse?.recentRuns ?? [])),
    },
    {
      label: `${race.distance}m 경험`,
      values: selected.map(({ horse }) => {
        const runs = horse?.recentRuns.filter((run) => run.distance === race.distance) ?? [];
        return runs.length > 0 ? `${runs.length}회 · ${formatTop3Rate(runs)}` : '표본 없음';
      }),
    },
    {
      label: '기수 최근 3위권',
      values: selected.map(({ jockey }) => jockey ? `${jockey.recentTop3Rate}%` : '정보 없음'),
    },
    {
      label: '부담중량',
      values: selected.map(({ entry }) => `${entry.carriedWeight}kg`),
    },
    {
      label: '마체중',
      values: selected.map(({ entry }) => entry.bodyWeight === null
        ? '정보 없음'
        : `${entry.bodyWeight}kg${entry.bodyWeightChange === null ? '' : ` (${entry.bodyWeightChange > 0 ? '+' : ''}${entry.bodyWeightChange})`}`),
    },
    {
      label: '레이팅',
      values: selected.map(({ entry }) => entry.rating === null ? '정보 없음' : String(entry.rating)),
    },
  ];

  return (
    <>
      <div className="compare-heading">
        <div>
          <h2>출전마 비교</h2>
          <span>{selectedGates.length} / 4 선택</span>
        </div>
      </div>
      <div className="entry-selector" aria-label="비교할 출전마">
        {race.entries.map((entry) => {
          const checked = selectedGates.includes(entry.gate);
          const disabled = !checked && selectedGates.length >= 4;
          return (
            <div className={`entry-select-row${checked ? ' selected' : ''}`} key={entry.gate}>
              <label>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleEntry(entry.gate)} />
                <span className="gate-number">{entry.gate}</span>
                <span>
                  <strong>{entry.horseName}</strong>
                  <small>{entry.jockeyName} · {entry.carriedWeight}kg</small>
                </span>
              </label>
              <Link to={`/horse/${entry.horseId}?race=${race.id}`} aria-label={`${entry.horseName} 상세 정보`}>
                <ChevronRight size={19} />
              </Link>
            </div>
          );
        })}
      </div>

      {selected.length >= 2 ? (
        <div className="comparison-scroll" aria-label="선택한 출전마 비교표">
          <div className="comparison-table" style={{ '--compare-columns': selected.length } as CSSProperties}>
            <div className="comparison-corner">비교 기준</div>
            {selected.map(({ entry }) => (
              <div className="comparison-horse" key={`head-${entry.gate}`}>
                <span>{entry.gate}</span>
                <HorseDetailLink race={race} gate={entry.gate}>
                  <strong>{entry.horseName}</strong>
                </HorseDetailLink>
              </div>
            ))}
            {comparisonRows.map((row) => (
              <div className="comparison-grid-row" key={row.label}>
                <strong>{row.label}</strong>
                {row.values.map((value, index) => <span key={`${row.label}-${selected[index].entry.gate}`}>{value}</span>)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="compare-required" role="status">
          <GitCompareArrows size={22} />
          <strong>2두 이상 선택해 주세요</strong>
          <span>선택한 말의 기록을 같은 기준으로 비교해요.</span>
        </div>
      )}
      <p className="panel-footnote">값과 표본 수를 함께 비교하며, 기록이 없는 항목은 그대로 구분해요.</p>
    </>
  );
};

const ResultsPanel = ({ race }: { race: Race }) => {
  return (
    <>
      {race.oddsSnapshot && <OddsSnapshotPanel snapshot={race.oddsSnapshot} race={race} />}
      {race.odds.length > 0 && (
        <section className="result-odds-section primary">
          <SectionHeading title="확정 조합·배당" />
          <ConfirmedOddsGrid odds={race.odds} />
        </section>
      )}
      {race.results.length > 0 ? (
        <>
          <SectionHeading title="도착 기록" />
          <div className="result-list">
            {race.results.map((result) => (
              <div className="result-row" key={result.rank}>
                <span className={`result-rank rank-${result.rank}`}>{result.rank}</span>
                <div>
                  <HorseDetailLink race={race} gate={result.gate}>
                    <strong>{result.horseName}</strong>
                  </HorseDetailLink>
                  <span>{result.jockeyName} · {result.gap}</span>
                </div>
                <div><strong>{result.record}</strong><span>G1F {result.g1f ?? '-'}</span></div>
              </div>
            ))}
          </div>
          <p className="panel-footnote">구간 기록이 원천 데이터에 없으면 ‘정보 없음’으로 유지해요.</p>
        </>
      ) : (
        <EmptyState
          title={race.status === 'awaiting_result' ? '확정 기록을 확인하고 있어요' : '아직 경기 전이에요'}
          detail={race.oddsSnapshot ? '현재 배당을 확인하고, 출전마 기록은 비교 화면에서 볼 수 있어요.' : '경기 결과가 확정되면 도착 기록을 보여드려요.'}
        />
      )}
    </>
  );
};

const AnalysisPanel = ({ race }: { race: Race }) => {
  if (race.analysisStatus !== 'ready' || !race.report) {
    const status = race.analysisStatus === 'ready' ? 'pending' : race.analysisStatus;
    return (
      <div className="analysis-state">
        <AnalysisStatusBadge status={status} />
        <h2>{analysisStatusCopy[status].label}</h2>
        <p>{analysisStatusCopy[status].detail}</p>
        <Link className="secondary-button" to={`/race/${race.id}?tab=compare`}>
          출전마 직접 비교
          <GitCompareArrows size={18} />
        </Link>
      </div>
    );
  }

  return <ReportEntryPanel race={race} report={race.report} />;
};

const reportTopThree = (report: Report) => {
  if (report.metricMode === 'score') {
    return [...report.scores]
      .sort((left, right) => right.overall - left.overall || left.gate - right.gate)
      .slice(0, 3)
      .map((item) => ({ ...item, valueLabel: `분석 ${item.overall}점` }));
  }
  return [...report.probabilities]
    .sort((left, right) => right.top3 - left.top3 || right.win - left.win || left.gate - right.gate)
    .slice(0, 3)
    .map((item) => ({ ...item, valueLabel: `3위권 ${item.top3}%` }));
};

const ReportEntryPanel = ({ race, report }: { race: Race; report: Report }) => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(() => isRaceReportUnlocked(race.id));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expected = reportTopThree(report);

  useEffect(() => {
    setUnlocked(isRaceReportUnlocked(race.id));
  }, [race.id]);

  const goToReport = () => {
    navigate(`/race/${race.id}/report`, {
      state: { backTo: `/race/${race.id}?tab=analysis` },
    });
  };

  const openReport = async () => {
    setBusy(true);
    setError(null);
    const outcome = await showRewardedAd();
    setBusy(false);
    if (outcome === 'completed' || outcome === 'preview') {
      unlockRaceReport(race.id);
      setUnlocked(true);
      setOpen(false);
      goToReport();
      return;
    }
    setError(outcome === 'dismissed' ? '광고 완료가 확인되지 않았어요.' : '광고를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
  };

  return (
    <div className="report-entry">
      <div className="report-entry-heading">
        <span className="lock-visual">{unlocked ? <FileChartColumn size={27} /> : <LockKeyhole size={27} />}</span>
        <div>
          <div><AnalysisStatusBadge status="ready" />{unlocked && <span className="viewed-badge">열람 완료</span>}</div>
          <h2>말랑노트 예상 분석</h2>
          <p>1~3위권에서 함께 살펴볼 3두와 선정 근거를 정리했어요.</p>
        </div>
      </div>
      <div className={`report-entry-preview${unlocked ? '' : ' locked'}`} aria-label={unlocked ? '예상 3두 미리보기' : '예상 3두 잠금 미리보기'}>
        {unlocked
          ? expected.map((item) => (
            <div key={item.gate}>
              <i>{item.gate}</i>
              <HorseDetailLink race={race} gate={item.gate}>
                <strong>{item.horseName}</strong>
              </HorseDetailLink>
            </div>
          ))
          : [1, 2, 3].map((slot) => (
            <div key={slot}><i><LockKeyhole size={14} /></i><strong>선정 완료</strong></div>
          ))}
      </div>
      <button
        className="primary-button full-width"
        type="button"
        onClick={unlocked ? goToReport : () => setOpen(true)}
      >
        {unlocked ? '분석 리포트 다시 보기' : '분석 리포트 보기'}
        <FileChartColumn size={18} />
      </button>
      <p className="report-entry-note">
        {unlocked ? '이 경주 리포트는 광고 없이 계속 다시 볼 수 있어요.' : '최초 열람 시 보상형 광고 완료 후 이 경주 리포트가 계속 열려요.'}
      </p>
      <ModalSheet
        open={open}
        title="분석 리포트 열기"
        busy={busy}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button className="text-button" type="button" onClick={() => setOpen(false)} disabled={busy}>취소</button>
            <button className="primary-button" type="button" onClick={openReport} disabled={busy}>{busy ? '광고 준비 중' : '계속'}</button>
          </>
        }
      >
        <p>보상형 광고를 완료하면 이 경주의 분석 리포트를 계속 다시 볼 수 있어요.</p>
        <p className="muted-copy">광고를 닫거나 완료되지 않으면 리포트는 열리지 않아요.</p>
        {adMode === 'web-preview' && <p className="preview-note">웹 검수에서는 광고 완료 이벤트만 시뮬레이션해요.</p>}
        {error && <p className="inline-error"><CircleAlert size={17} />{error}</p>}
      </ModalSheet>
    </div>
  );
};

const ExpectedTopThree = ({ report, race }: { report: Report; race: Race }) => {
  const expected = reportTopThree(report);
  return (
    <section className="expected-three-section" aria-labelledby="expected-three-title">
      <div className="expected-three-heading">
        <h2 id="expected-three-title">1~3위권 예상 3두</h2>
        <span>순서 없음</span>
      </div>
      <div className="expected-three-list">
        {expected.map((item) => {
          const score = report.scores.find((candidate) => candidate.gate === item.gate);
          const perspectiveCandidate = report.perspectives
            .flatMap((perspective) => perspective.candidates)
            .find((candidate) => candidate.gate === item.gate);
          const reasons = perspectiveCandidate?.reasons.slice(0, 2)
            ?? (score ? [
              `최근 기록 점수 ${score.recentForm}점`,
              `거리 적합 점수 ${score.distanceFit}점`,
            ] : []);
          const caution = perspectiveCandidate?.cautions[0]
            ?? (score && score.sampleCount < 3 ? `비교 가능한 최근 기록이 ${score.sampleCount}회로 적어요.` : null);
          return (
            <article className="expected-three-row" key={item.gate}>
              <div className="expected-horse-heading">
                <i>{item.gate}</i>
                <HorseDetailLink race={race} gate={item.gate}>
                  <strong>{item.horseName}</strong>
                </HorseDetailLink>
                <b>{item.valueLabel}</b>
              </div>
              {reasons.map((reason) => <p key={reason}>{reason}</p>)}
              {caution && <span>{caution}</span>}
            </article>
          );
        })}
      </div>
    </section>
  );
};

const ReportPage = () => {
  const { raceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useAppData();
  const race = data.races.find((item) => item.id === raceId);
  const backTo = (location.state as { backTo?: string } | null)?.backTo
    ?? (race ? `/race/${race.id}?tab=analysis` : '/races');

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [raceId]);

  if (!race || race.analysisStatus !== 'ready' || !race.report) {
    return <Navigate to={race ? `/race/${race.id}?tab=analysis` : '/races'} replace />;
  }
  if (!isRaceReportUnlocked(race.id)) {
    return <Navigate to={`/race/${race.id}?tab=analysis`} replace />;
  }

  return (
    <DetailLayout
      title="분석 리포트"
      subtitle={`${race.meet} ${race.number}경주 · ${formatKoreanDate(race.date)}`}
      onBack={() => navigate(backTo, { replace: true })}
    >
      <NoticeBand />
      <section className="report-intro">
        <h1>말랑노트 예상 분석</h1>
        <strong>{race.meet} {race.number}경주</strong>
        <p>기록과 수집 시점 데이터를 바탕으로 1~3위권에서 함께 살펴볼 3두를 정리했어요.</p>
      </section>
      <div className="report-safety-note" role="note">
        <CircleAlert size={19} />
        <p>
          공공데이터 기반 통계 분석이며 실제 결과를 보장하지 않아요.
          마권 구매나 금전적 행동을 권유하지 않으며, 표시된 3두의 순서는 예상 도착 순서가 아니에요.
        </p>
      </div>
      <section className="report-page-content">
        <ExpectedTopThree report={race.report} race={race} />
        <ReportContent report={race.report} race={race} completed={race.status === 'completed'} />
      </section>
    </DetailLayout>
  );
};

const ReportContent = ({ report, race, completed }: { report: Report; race: Race; completed: boolean }) => (
  <div className="report-content">
    <div className="report-meta">
      <BarChart3 size={20} />
      <div>
        <strong>{completed ? '사전 분석 스냅샷' : report.metricMode === 'score' ? '시험 분석 점수' : '사전 분석'}</strong>
        <span>{formatDateTime(report.createdAt)} 생성 · 결과 미반영</span>
      </div>
    </div>
    <h2>{report.headline}</h2>
    <p className="method-copy">{report.sampleRange}</p>
    {report.metricMode === 'score' ? (
      <div className="score-table">
        <div className="score-head"><span>말</span><span>종합</span><span>최근</span><span>거리</span></div>
        {report.scores.map((item) => (
          <div className="score-row" key={item.gate}>
            <HorseDetailLink race={race} gate={item.gate}>
              <strong><i>{item.gate}</i>{item.horseName}</strong>
            </HorseDetailLink>
            <span>{item.overall}</span>
            <span>{item.recentForm}</span>
            <span>{item.distanceFit}</span>
          </div>
        ))}
        <p>100점 기준 상대 비교값 · 확률 아님</p>
      </div>
    ) : (
      <div className="probability-table">
        <div className="probability-head"><span>말</span><span>1위</span><span>2위권</span><span>3위권</span></div>
        {report.probabilities.map((item) => (
          <div className="probability-row" key={item.gate}>
            <HorseDetailLink race={race} gate={item.gate}>
              <strong><i>{item.gate}</i>{item.horseName}</strong>
            </HorseDetailLink>
            <span>{item.win}%</span><span>{item.top2}%</span><span>{item.top3}%</span>
          </div>
        ))}
      </div>
    )}
    {report.perspectives.length > 0 && (
      <section className="perspective-section">
        <SectionHeading title="관점별 해석" />
        <div className="perspective-list">
          {report.perspectives.map((perspective) => (
            <article className={`perspective-row ${perspective.type}`} key={perspective.type}>
              <div className="perspective-heading">
                <h3>{perspective.title}</h3>
                <span>{perspective.candidates.length}두</span>
              </div>
              <p>{perspective.summary}</p>
              <div className="perspective-candidates">
                {perspective.candidates.map((candidate) => (
                  <div key={`${perspective.type}-${candidate.gate}`}>
                    <HorseDetailLink race={race} gate={candidate.gate}>
                      <strong><i>{candidate.gate}</i>{candidate.horseName}<small>{candidate.role}</small></strong>
                    </HorseDetailLink>
                    {candidate.reasons.map((reason) => <span key={reason}>{reason}</span>)}
                    {candidate.cautions.map((caution) => <em key={caution}>{caution}</em>)}
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    )}
    <SectionHeading title="판단 근거" />
    <div className="signal-list">
      {report.signals.map((signal) => (
        <div className={`signal-row ${signal.tone}`} key={signal.title}><span /><div><strong>{signal.title}</strong><p>{signal.detail}</p></div></div>
      ))}
    </div>
    <div className="limitations"><strong>해석할 때 확인하세요</strong>{report.limitations.map((item) => <p key={item}>{item}</p>)}</div>
  </div>
);

const findContextRace = (raceId: string | null, races: Race[]): Race | undefined => races.find((race) => race.id === raceId);

const HorseDetailPage = () => {
  const { horseId } = useParams();
  const [params] = useSearchParams();
  const { data } = useAppData();
  const horse = data.horses.find((item) => item.id === horseId);
  if (!horse) return <Navigate to="/races" replace />;
  return <ParticipantDetail kind="horse" item={horse} contextRace={findContextRace(params.get('race'), data.races)} />;
};

const JockeyDetailPage = () => {
  const { jockeyId } = useParams();
  const [params] = useSearchParams();
  const { data } = useAppData();
  const jockey = data.jockeys.find((item) => item.id === jockeyId);
  if (!jockey) return <Navigate to="/races" replace />;
  return <ParticipantDetail kind="jockey" item={jockey} contextRace={findContextRace(params.get('race'), data.races)} />;
};

const ParticipantDetail = ({ kind, item, contextRace }: { kind: 'horse'; item: Horse; contextRace?: Race } | { kind: 'jockey'; item: Jockey; contextRace?: Race }) => {
  const [showHistory, setShowHistory] = useState(false);
  const isHorse = kind === 'horse';
  const stats = isHorse
    ? [
        ['국적·성별', `${item.country} · ${item.sex}`], ['나이', `${item.age}세`],
        ['레이팅', item.rating ?? '정보 없음'], ['조교사', item.trainer],
      ]
    : [
        ['소속조', item.stable], ['통산 출전', `${item.careerStarts}회`],
        ['통산 1위', `${item.careerWins}회`], ['최근 3위권', `${item.recentTop3Rate}%`],
      ];

  return (
    <DetailLayout title={item.name} subtitle={isHorse ? '경주마 정보' : '기수 정보'}>
      <NoticeBand />
      {isHorse ? (
        <figure className="horse-profile-hero">
          <img src={horseImageForId(item.id)} alt="" loading="eager" />
          <figcaption>
            <span>말랑노트 대표 이미지 · 실제 개체 사진 아님</span>
            <h1>{item.name}</h1>
            <p>{item.country} {item.sex} · {item.age}세</p>
          </figcaption>
        </figure>
      ) : (
        <section className="profile-band">
          <span><UserRound size={25} /></span>
          <div><h1>{item.name}</h1><p>{item.stable} · 최근 3위권 {item.recentTop3Rate}%</p></div>
        </section>
      )}
      {contextRace && (
        <Link className="context-race-link" to={`/race/${contextRace.id}?tab=compare`}>
          <div><span>이번 출전</span><strong>{contextRace.meet} {contextRace.number}경주 · {contextRace.distance}m</strong></div><ChevronRight size={19} />
        </Link>
      )}
      <section className="content-section profile-stats">
        <SectionHeading title="기본 정보" />
        <div className="table-block">{stats.map(([label, value]) => <TableRow key={String(label)} label={String(label)} value={value} />)}</div>
      </section>
      <section className="content-section history-section">
        <SectionHeading title="최근 기록" />
        {!showHistory ? (
          <>
            {item.recentRuns[0] && <HistoryRow run={item.recentRuns[0]} />}
            <InterstitialAction
              label="상세 기록 보기"
              sheetTitle="상세 기록 열기"
              description="최근 상세 기록을 열기 전에 전면 광고가 표시될 수 있어요."
              onContinue={() => setShowHistory(true)}
            />
          </>
        ) : (
          <div className="history-list">{item.recentRuns.map((run) => <HistoryRow key={`${run.date}-${run.distance}`} run={run} />)}</div>
        )}
      </section>
      <AnalysisDisclaimer />
    </DetailLayout>
  );
};

const HistoryRow = ({ run }: { run: Horse['recentRuns'][number] }) => (
  <div className="history-row">
    <div><strong>{formatKoreanDate(run.date)}</strong><span>{run.meet} · {run.distance}m</span></div>
    <div><strong>{formatRank(run.rank)}</strong><span>{run.record ?? '기록 없음'} · {run.runners}두</span></div>
  </div>
);

const AnalysisDisclaimer = () => (
  <footer className="analysis-disclaimer">
    <Info size={17} />
    <p>공공데이터 기반 통계 분석으로 경기 결과를 보장하지 않아요.</p>
  </footer>
);

const NotFoundPage = () => (
  <DetailLayout title="페이지 없음"><EmptyState title="화면을 찾을 수 없어요" detail="이전 화면으로 돌아가 다시 확인해 주세요." /></DetailLayout>
);

export const MallangApp = () => {
  const loading = useAppLoading();
  if (loading) return <LoadingScreen />;
  return (
    <Routes>
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/races" element={<RacesPage />} />
        <Route path="/data" element={<Navigate to="/" replace />} />
      </Route>
      <Route path="/race/:raceId" element={<RaceDetailPage />} />
      <Route path="/race/:raceId/report" element={<ReportPage />} />
      <Route path="/horse/:horseId" element={<HorseDetailPage />} />
      <Route path="/jockey/:jockeyId" element={<JockeyDetailPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};
